// frontend/src/pages/portfolio/sections/DesktopAppSection.tsx
import React from "react";
import { CheckCircle2, Download, Monitor, ShieldCheck } from "lucide-react";
import { getMicrosoftStoreUrl } from "../../../utils/storeUrl";
import SectionEyebrow from "../components/SectionEyebrow";
import { container, sectionHeading, sectionReveal } from "../components/styles";

const appPoints = [
  "OS-level idle detection",
  "Auto-syncs while you work",
  "Periodic screenshots (when enabled)",
  "Quiet system-tray presence",
];

const DesktopAppSection: React.FC = () => {
  return (
    <section
      id="desktop-app"
      className="border-t border-primary-200/30 bg-primary-50/30 py-16 sm:py-20"
      aria-labelledby="desktop-heading"
    >
      <div className={container}>
        <div className={`grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16 ${sectionReveal}`}>
          <div>
            <SectionEyebrow icon={Monitor}>Desktop app</SectionEyebrow>
            <h2 id="desktop-heading" className={`mt-4 ${sectionHeading}`}>
              Track activity accurately, without thinking about it.
            </h2>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-gray-600">
              The Time Tracker desktop app runs in your tray and captures
              activity with OS-level accuracy. Free with every account.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {appPoints.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-600" aria-hidden />
                  <span className="text-sm leading-relaxed text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              <a
                href={getMicrosoftStoreUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-primary-900 px-6 py-3 text-base font-semibold text-white shadow-elevated hover:bg-primary-800 transition-colors min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                <Download className="h-5 w-5" aria-hidden />
                Get on Microsoft Store
              </a>
              <a
                href="#product"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-primary-200 bg-white px-6 py-3 text-base font-medium text-primary-900 min-h-[48px] hover:bg-primary-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                Or use it in the browser
              </a>
            </div>
            <p className="mt-4 inline-flex items-center gap-2 text-xs text-primary-700">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
              Browser tracking available when admins enable it.
            </p>
          </div>

          {/* App illustration */}
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2.25rem] bg-gradient-to-br from-primary-300/40 via-transparent to-primary-500/20 blur-3xl motion-reduce:hidden" aria-hidden />
            <div className="relative mx-auto aspect-square w-full max-w-sm rounded-3xl border border-gray-200 bg-gradient-to-br from-white to-slate-50 p-8 shadow-modal ring-1 ring-primary-900/[0.05]">
              <div className="flex h-full flex-col">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-error-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-warning-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-success-300" />
                </div>
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-primary-900 text-white shadow-elevated ring-1 ring-primary-800/40">
                    <Monitor className="h-10 w-10" aria-hidden />
                  </div>
                  <p className="mt-6 font-display text-lg font-semibold text-primary-950">
                    Time Tracker
                  </p>
                  <p className="text-xs text-primary-700">Running quietly · 0m idle</p>
                  <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-success-50 px-3 py-1 text-xs font-semibold text-success-700 ring-1 ring-success-200">
                    <span className="h-2 w-2 rounded-full bg-success-500 motion-safe:animate-pulse" />
                    Connected
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DesktopAppSection;
